import { debounce } from 'ts-debounce'

import { parseEstimateList } from '$lib/parsers'
import { apiPostJson } from '$lib/proxy/apiProxy'
import { EstimateStatus } from '$lib/stores/estimate/types'
import type { EstimatesFilter, EstimatesParams } from '$lib/stores/estimates_list/types'
import { StatusFilter } from '$lib/stores/estimates_list/types'
import type { EstimateListItem } from '$lib/types/client_types'

export const filters: EstimatesFilter[] = [
	{
		name: 'All',
		data_test_id: 'filter_all',
		slug: StatusFilter.All,
		statuses: [
			EstimateStatus.Draft,
			EstimateStatus.Sent,
			EstimateStatus.Viewed,
			EstimateStatus.Invoiced,
			EstimateStatus.Approved
		],
		archived: [false]
	},
	{
		name: 'Unsent',
		data_test_id: 'filter_unsent',
		slug: StatusFilter.Unsent,
		statuses: [EstimateStatus.Draft],
		archived: [false]
	},
	{
		name: 'Sent',
		data_test_id: 'filter_sent',
		slug: StatusFilter.Sent,
		statuses: [EstimateStatus.Sent],
		archived: [false]
	},
	{
		name: 'Approved',
		data_test_id: 'filter_approved',
		slug: StatusFilter.Approved,
		statuses: [EstimateStatus.Approved],
		archived: [false]
	},
	{
		name: 'Invoiced',
		data_test_id: 'filter_invoiced',
		slug: StatusFilter.Invoiced,
		statuses: [EstimateStatus.Invoiced],
		archived: [false]
	},
	{
		name: 'Archived',
		data_test_id: 'filter_archived',
		slug: StatusFilter.Archived,
		statuses: [
			EstimateStatus.Draft,
			EstimateStatus.Sent,
			EstimateStatus.Invoiced,
			EstimateStatus.Approved
		],
		archived: [true]
	}
]

export const getFilter = (slug: StatusFilter): EstimatesFilter => {
	const filter = filters.find((filter) => filter.slug === slug)
	if (!filter) {
		throw new Error('Could not find filter ' + slug)
	}
	return filter
}

let controller: AbortController | null
type GetResult = Promise<EstimateListItem[] | null>

export const getEstimates = async (params: EstimatesParams): GetResult => {
	//	console.log('estimate parameters', params);

	if (controller) {
		controller.abort()
	}
	controller = new AbortController()
	const signal = controller.signal

	const jsonData = await apiPostJson(
		'/estimates',
		{
			statuses: params.statuses,
			archived: params.archived,
			query: params.query,
			client_id: params.clientId,
			item_id: params.itemId,
			estimate_id: params.estimateId,
			date_start: params.date_start,
			date_end: params.date_end
		},
		signal
	)
	controller = null

	// Check if cancelled by AbortController
	if (jsonData === null) return []
	if (jsonData?.error) {
		return []
	}

	const data = parseEstimateList(jsonData.estimates)
	return data
}

export const debouncedFetchEstimates = debounce(getEstimates, 50, {
	isImmediate: true,
	maxWait: 100
})

// Ts => Time Since
export const getTsAction = (e: EstimateListItem): number => {
	if (e.status === EstimateStatus.Deleted) return e.last_deleted_at || 0
	else if (e.status === EstimateStatus.Draft) return e.created_at || 0
	else if (e.status === EstimateStatus.Approved) return e.approved_at || 0
	else if (e.status === EstimateStatus.Invoiced) return e.invoiced_at || 0
	else if (e.status === EstimateStatus.Sent) return e.last_sent_at || 0
	else if (e.status === EstimateStatus.Viewed) return e.last_viewed_at || 0
	else throw new Error('Unexpected estimate status ' + e.status)
}
